import { useEffect, useMemo, useRef, useState } from 'react';
import { DebugPanel } from './components/DebugPanel';
import { DownloadButton } from './components/DownloadButton';
import { ErrorPanel } from './components/ErrorPanel';
import { ShortcutInstructions } from './components/ShortcutInstructions';
import { Wallpaper } from './components/Wallpaper';
import { decodeBase64Yaml, loadConfigFromUrl, parseYamlConfig, type LoadResult } from './config/loader';
import type { WallpaperConfig } from './config/schema';
import { todayFromOverride } from './lib/dates';

const CONFIG_URL = '/config.yaml';

export function App() {
  const [result, setResult] = useState<LoadResult | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const debug = params.get('debug') === '1';
  const dateOverride = params.get('date');
  const configB64 = params.get('config');
  const today = todayFromOverride(dateOverride);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (configB64) {
        try {
          const yamlText = decodeBase64Yaml(configB64);
          if (cancelled) return;
          setRawText(yamlText);
          setResult(parseYamlConfig(yamlText));
          return;
        } catch (e) {
          if (!cancelled) {
            setResult({ ok: false, error: `bad ?config= base64: ${(e as Error).message}` });
          }
          return;
        }
      }
      const r = await fetch(CONFIG_URL, { cache: 'no-cache' });
      const text = r.ok ? await r.text() : '';
      if (cancelled) return;
      setRawText(text || null);
      setResult(r.ok ? parseYamlConfig(text) : await loadConfigFromUrl(CONFIG_URL));
    })();
    return () => {
      cancelled = true;
    };
  }, [configB64]);

  useEffect(() => {
    if (result?.ok) {
      (window as unknown as { __wallpaper: { config: WallpaperConfig; today: Date } }).__wallpaper = {
        config: result.config,
        today,
      };
      // eslint-disable-next-line no-console
      console.log('[life-wallpaper] config', result.config);
    }
  }, [result, today]);

  if (!result) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">loading…</div>;
  }
  if (!result.ok) {
    return <ErrorPanel error={result.error} issues={result.issues} raw={result.raw} />;
  }

  const { config } = result;

  return (
    <div className="min-h-screen w-full flex flex-col items-center gap-4 p-4 sm:p-8">
      <div
        className="relative w-full max-w-[420px] aspect-[var(--aspect)] shadow-2xl rounded-[2rem] overflow-hidden ring-1 ring-zinc-800"
        style={{ ['--aspect' as string]: `${config.device.width} / ${config.device.height}` }}
      >
        <Wallpaper ref={svgRef} config={config} today={today} />
      </div>
      <ShortcutInstructions />
      <div className="fixed bottom-6 right-6 z-40">
        <DownloadButton
          getSvg={() => svgRef.current}
          width={config.device.width}
          height={config.device.height}
          filename={`life-wallpaper-${config.mode}-${today.toISOString().slice(0, 10)}.png`}
        />
      </div>
      {debug && <DebugPanel config={config} today={today} configUrl={CONFIG_URL} rawText={rawText} />}
    </div>
  );
}
